import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { icon, Map as LeafletMap } from 'leaflet';
import { MapPin } from 'lucide-react';

// Fix for default marker icon in Leaflet with React
const customIcon = icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

interface LocationMapProps {
  address: string;
  name: string;
}

interface GeocodingResult {
  lat: number;
  lon: number;
  display_name: string;
}

// Component to handle map initialization and prevent zoom issues
const MapController = ({ center, zoom }: { center: [number, number], zoom: number }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!map) return;

    // Wait for map to be ready
    const initializeMap = () => {
      if (map && map._loaded) {
        map.invalidateSize();
        map.setView(center, zoom);
        map.scrollWheelZoom.enable();
      }
    };

    // Initial setup with delay
    const timer = setTimeout(initializeMap, 250);

    // Cleanup
    return () => clearTimeout(timer);
  }, [map, center, zoom]);
  
  return null;
};

const LocationMap: React.FC<LocationMapProps> = ({ address, name }) => {
  const [coordinates, setCoordinates] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [mounted, setMounted] = useState(false);
  const mapRef = useRef<LeafletMap | null>(null);

  // Format address to improve geocoding success rate
  const formatAddress = (address: string): string => {
    // Remove any extra spaces, commas, etc.
    let formatted = address.trim();
    
    // Make sure the address has a country if not specified
    if (!formatted.toLowerCase().includes('deutschland') && 
        !formatted.toLowerCase().includes('germany')) {
      formatted += ', Deutschland';
    }
    
    // Fix common typos in addresses
    formatted = formatted.replace('Rathauspassge', 'Rathauspassage');
    
    return formatted;
  };

  useEffect(() => {
    const geocodeAddress = async () => {
      if (!address) {
        setError('Keine Adresse angegeben');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Format the address to improve geocoding success
        const formattedAddress = formatAddress(address);
        const encodedAddress = encodeURIComponent(formattedAddress);
        
        // Use OpenStreetMap Nominatim API for geocoding
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`,
          {
            headers: {
              'Accept-Language': 'de',
              'User-Agent': 'AuftragsManager/1.0'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Geocoding-Anfrage fehlgeschlagen');
        }

        const data: GeocodingResult[] = await response.json();
        
        if (data.length === 0) {
          // If no results, try a more generic search by removing specific details
          if (retryCount === 0) {
            setRetryCount(1);
            // Try again with a more generic address (e.g., just city and country)
            const parts = formattedAddress.split(',');
            if (parts.length > 1) {
              // Take just the city and country parts
              const simplifiedAddress = parts.slice(Math.max(0, parts.length - 2)).join(',');
              const encodedSimplified = encodeURIComponent(simplifiedAddress);
              
              const retryResponse = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodedSimplified}&limit=1`,
                {
                  headers: {
                    'Accept-Language': 'de',
                    'User-Agent': 'AuftragsManager/1.0'
                  }
                }
              );
              
              if (retryResponse.ok) {
                const retryData: GeocodingResult[] = await retryResponse.json();
                if (retryData.length > 0) {
                  const { lat, lon } = retryData[0];
                  setCoordinates([parseFloat(lat), parseFloat(lon)]);
                  setMounted(true);
                  setLoading(false);
                  return;
                }
              }
            }
          }
          
          setError('Adresse konnte nicht gefunden werden');
          setLoading(false);
          return;
        }

        const { lat, lon } = data[0];
        setCoordinates([parseFloat(lat), parseFloat(lon)]);
        setMounted(true);
      } catch (err) {
        console.error('Fehler beim Geocoding:', err);
        setError('Fehler beim Laden der Karte');
      } finally {
        setLoading(false);
      }
    };

    geocodeAddress();
  }, [address, retryCount]);

  // Reset retry count when address changes
  useEffect(() => {
    setRetryCount(0);
  }, [address]);

  // Ensure map is properly sized when component mounts
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Handle map initialization
  const handleMapCreated = (map: LeafletMap) => {
    mapRef.current = map;
    
    // Enable scroll wheel zoom
    map.scrollWheelZoom.enable();
    
    // Force a resize event after the map is created
    setTimeout(() => {
      if (mapRef.current && mapRef.current._loaded) {
        mapRef.current.invalidateSize();
      }
    }, 250);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-100 rounded-md">
        <div className="text-gray-500">Karte wird geladen...</div>
      </div>
    );
  }

  if (error || !coordinates) {
    return (
      <div className="flex flex-col items-center justify-center h-48 bg-gray-100 rounded-md">
        <MapPin size={32} className="text-gray-400 mb-2" />
        <div className="text-gray-500">{error || 'Standort konnte nicht gefunden werden'}</div>
        <div className="text-xs text-gray-400 mt-2">Adresse: {address}</div>
      </div>
    );
  }

  // Default to Hamburg if no coordinates are provided
  const hamburgCoordinates: [number, number] = [53.5511, 9.9937]; // Hamburg coordinates
  const displayCoordinates = coordinates || hamburgCoordinates;

  return (
    <div className="h-48 rounded-md overflow-hidden border border-gray-200">
      <MapContainer
        center={displayCoordinates}
        zoom={15}
        style={{ height: '100%', width: '100%' }}
        whenCreated={handleMapCreated}
        scrollWheelZoom={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <Marker position={displayCoordinates} icon={customIcon}>
          <Popup>{name}<br />{address}</Popup>
        </Marker>
        <MapController center={displayCoordinates} zoom={15} />
      </MapContainer>
    </div>
  );
};

export default LocationMap;